import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Loader2, XCircle, CheckCircle2, Shield, Building2, ClipboardCheck, Scale, Camera, AlertTriangle, FileText, Info, Check, ArrowRight } from 'lucide-react';
import api from '../api';
import { useToast } from '../hooks/use-toast';

const CONCLUSION_STYLES = {
  recommended: { label: 'RECOMMANDÉ', bg: 'bg-green-500', text: 'text-white', border: 'border-green-500' },
  recommended_with_conditions: { label: 'RECOMMANDÉ SOUS CONDITIONS', bg: 'bg-amber-500', text: 'text-white', border: 'border-amber-500' },
  more_verification: { label: 'VÉRIFICATIONS SUPPLÉMENTAIRES REQUISES', bg: 'bg-blue-500', text: 'text-white', border: 'border-blue-500' },
  not_recommended: { label: 'NON RECOMMANDÉ', bg: 'bg-red-500', text: 'text-white', border: 'border-red-500' },
};

const COMPLIANCE_STYLES = {
  conforme: 'bg-green-100 text-green-800 border-green-400',
  non_conforme: 'bg-red-100 text-red-800 border-red-400',
  a_corriger: 'bg-amber-100 text-amber-800 border-amber-400',
  a_verifier: 'bg-blue-100 text-blue-800 border-blue-400',
  test_independant_requis: 'bg-purple-100 text-purple-800 border-purple-400',
};
const COMPLIANCE_LABELS = {
  conforme: 'CONFORME', non_conforme: 'NON CONFORME', a_corriger: 'À CORRIGER',
  a_verifier: 'À VÉRIFIER', test_independant_requis: 'TEST INDÉPENDANT REQUIS',
};

const INFO_STYLES = {
  declared: 'bg-gray-100 text-gray-700 border-gray-300',
  document_received: 'bg-blue-50 text-blue-700 border-blue-300',
  yuville_verified: 'bg-green-50 text-green-700 border-green-500',
  independent_verification_required: 'bg-purple-50 text-purple-700 border-purple-400',
  not_verified: 'bg-gray-50 text-gray-500 border-gray-300',
  anomaly_to_resolve: 'bg-red-50 text-red-700 border-red-500',
};
const INFO_LABELS = {
  declared: 'Déclaré par le manufacturier', document_received: 'Document reçu',
  yuville_verified: 'Vérifié par Yuville', independent_verification_required: 'Vérification indépendante requise',
  not_verified: 'Non vérifié', anomaly_to_resolve: 'Anomalie à résoudre',
};

const ISSUE_LABELS = { open: 'OUVERT', in_progress: 'EN COURS', resolved: 'RÉSOLU', accepted_by_client: 'ACCEPTÉ' };

const FIELD_A = [
  ['factoryName', 'Nom de l\'usine'], ['address', 'Adresse'], ['visitDate', 'Date de visite'],
  ['auditor', 'Auditeur'], ['peopleMet', 'Personnes rencontrées'],
  ['installations', 'Installations'], ['productionLines', 'Lignes de production'],
  ['equipments', 'Équipements'], ['headcount', 'Effectifs'],
  ['declaredCapacity', 'Capacité déclarée'], ['observedCapacity', 'Capacité constatée'],
  ['organization', 'Organisation'], ['qualityProcedures', 'Contrôle qualité'],
  ['storage', 'Entreposage'], ['packaging', 'Conditionnement'],
  ['traceability', 'Traçabilité'], ['similarExperience', 'Expérience comparable'],
  ['capacityFit', 'Respect volumes/délais'],
];

const FIELD_C = [
  ['chineseName', 'Raison sociale (chinois)'], ['englishName', 'Raison sociale (anglais)'],
  ['uscc', 'Unified Social Credit Code'], ['businessLicense', 'Licence commerciale'],
  ['foundationDate', 'Date de constitution'], ['registeredAddress', 'Adresse enregistrée'],
  ['legalRep', 'Représentant légal'], ['businessScope', 'Activités enregistrées'],
  ['entityStatus', 'Statut'], ['directExportCapacity', 'Export direct'],
  ['exporter', 'Exportateur (si indirect)'], ['licenses', 'Licences'],
  ['certifications', 'Certifications'], ['certifyingBodies', 'Organismes'],
  ['ipElements', 'Propriété intellectuelle'], ['legalIssues', 'Litiges'],
  ['otherCompliance', 'Autres éléments'],
];

const DECISIONS = [
  { k: 'accept', label: 'ACCEPTER CE MANUFACTURIER', color: 'bg-green-500 hover:bg-green-600', icon: CheckCircle2 },
  { k: 'request_corrections', label: 'DEMANDER DES CORRECTIFS', color: 'bg-amber-500 hover:bg-amber-600', icon: AlertTriangle },
  { k: 'request_more_verification', label: 'VÉRIFICATIONS SUPPLÉMENTAIRES', color: 'bg-blue-500 hover:bg-blue-600', icon: Info },
  { k: 'reject', label: 'REJETER CE MANUFACTURIER', color: 'bg-red-500 hover:bg-red-600', icon: XCircle },
];

export default function AuditPage() {
  const { rfqId, mid } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get('token') || '';
  const { toast } = useToast();
  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [tab, setTab] = useState('summary');
  const [pending, setPending] = useState(''); // pending decision awaiting confirmation

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const res = await api.get(`/client/audit/${mid}?token=${encodeURIComponent(token)}`);
      setState({ loading: false, error: '', data: res.data });
    } catch (e) {
      setState({ loading: false, error: e?.response?.data?.detail || 'Lien invalide ou expiré.', data: null });
    }
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, [mid, token]);

  const submitDecision = async (decision) => {
    try {
      await api.post(`/client/audit/${mid}/decision?token=${encodeURIComponent(token)}`, { decision, confirmed: true });
      toast({ title: 'Décision enregistrée', duration: 8000 });
      setPending(''); load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible', duration: 10000 });
    }
  };

  if (state.loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="w-8 h-8 animate-spin text-brand-yellow" /></div>;
  if (state.error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
      <div className="max-w-md text-center bg-white p-8 rounded-xl shadow-md border">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-brand-navy mb-2">Accès impossible</h1>
        <p className="text-gray-600 text-sm mb-6">{state.error}</p>
        <Link to="/" className="text-brand-navy hover:text-brand-yellow text-sm font-semibold">← Retour à l'accueil</Link>
      </div>
    </div>
  );

  const { manufacturer, audit } = state.data;
  const conc = CONCLUSION_STYLES[audit.conclusion];
  const grid = audit.complianceGrid || [];
  const issues = audit.openIssues || [];
  const attachments = audit.attachments || [];
  const decided = !!audit.clientDecision;

  const entitiesConsistent = audit.factoryOperator && audit.factoryOperator === audit.contractingEntity && audit.contractingEntity === audit.invoicingEntity && audit.invoicingEntity === audit.paymentBeneficiary;

  const AUDIT_BASE = `${window.location.origin}/api/client/audit/${mid}`;
  const attUrl = (i) => `${AUDIT_BASE}/attachments/${i}?token=${encodeURIComponent(token)}`;

  const tabs = [
    { k: 'summary', label: 'Synthèse', icon: ClipboardCheck },
    { k: 'usine', label: 'A · Usine', icon: Building2 },
    { k: 'produit', label: 'B · Produit & Qualité', icon: Camera },
    { k: 'juridique', label: 'C · Juridique & Commercial', icon: Scale },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="bg-brand-navy text-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
          <Link to={`/dossier/${rfqId}?token=${token}`} className="text-brand-yellow text-xs font-bold tracking-widest uppercase">← Retour au dossier</Link>
          <div className="inline-flex items-center gap-2 mt-3 mb-2 px-3 py-1 rounded-full bg-brand-yellow/20 border border-brand-yellow text-brand-yellow text-[11px] font-bold tracking-widest uppercase">03 · Audit sur place</div>
          <h1 data-testid="audit-title" className="text-3xl md:text-4xl font-bold leading-tight">Rapport d'audit du manufacturier</h1>
          <p className="mt-2 text-white/80 text-lg font-semibold">{manufacturer.name}{manufacturer.city && <span className="text-white/60 font-normal"> · {manufacturer.city}</span>}</p>
        </div>
      </header>

      {/* Conclusion badge */}
      {conc && (
        <div className="max-w-6xl mx-auto px-6 lg:px-10 -mt-4">
          <div className={`rounded-xl shadow-lg p-6 ${conc.bg} ${conc.text}`}>
            <div className="text-[11px] tracking-widest font-bold opacity-80 mb-1">Conclusion Yuville</div>
            <div className="text-2xl md:text-3xl font-bold">{conc.label}</div>
            {audit.conclusionExplanation && <p className="mt-3 text-sm opacity-90 leading-relaxed max-w-4xl">{audit.conclusionExplanation}</p>}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-6 lg:px-10 mt-6">
        <div className="flex gap-1 overflow-x-auto border-b-2 border-gray-200">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.k} onClick={() => setTab(t.k)} className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-0.5 whitespace-nowrap transition ${tab === t.k ? 'border-brand-yellow text-brand-navy' : 'border-transparent text-gray-500 hover:text-brand-navy'}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 mt-6 space-y-6">
        {tab === 'summary' && (
          <>
            {/* Quick synthesis */}
            <section className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-bold text-brand-navy mb-4 flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-brand-yellow" /> Points clés</h2>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <SumItem label="Usine visitée" value={audit.factoryName || '—'} verif={audit.visitDate ? `Visite du ${audit.visitDate}` : ''} />
                <SumItem label="Capacité" value={audit.observedCapacity || audit.declaredCapacity || '—'} verif={audit.infoStatus?.observedCapacity ? INFO_LABELS[audit.infoStatus.observedCapacity] : ''} />
                <SumItem label="Conformité produit" value={`${grid.filter((r) => r.status === 'conforme').length} conforme(s) / ${grid.length} exigence(s)`} />
                <SumItem label="Contrôle qualité" value={audit.qualityProcedures ? '✓ documenté' : '—'} verif={audit.infoStatus?.qualityProcedures ? INFO_LABELS[audit.infoStatus.qualityProcedures] : ''} />
                <SumItem label="Certifications" value={audit.certifications || '—'} verif={audit.infoStatus?.certifications ? INFO_LABELS[audit.infoStatus.certifications] : ''} />
                <SumItem label="Situation juridique" value={audit.uscc ? `USCC ${audit.uscc}` : '—'} verif={audit.infoStatus?.uscc ? INFO_LABELS[audit.infoStatus.uscc] : ''} />
                <SumItem label="Cohérence des entités" value={entitiesConsistent ? '✓ Même entité pour usine, contrat, facture et paiement' : (audit.factoryOperator ? '⚠ Entités différentes — voir volet C' : '—')} />
                <SumItem label="Tests indépendants requis" value={grid.filter((r) => r.status === 'test_independant_requis').length + issues.filter((i) => (i.label || '').toLowerCase().includes('test')).length} />
              </div>
            </section>

            {/* Open issues */}
            {issues.length > 0 && (
              <section className="bg-amber-50 rounded-xl border-2 border-amber-400 p-6">
                <h2 className="font-bold text-amber-900 mb-4 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Points à résoudre avant contrat ({issues.length})</h2>
                <ul className="space-y-2">
                  {issues.map((it, i) => (
                    <li key={i} className="flex items-start gap-3 p-3 bg-white rounded border border-amber-200">
                      <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full font-bold ${it.status === 'resolved' || it.status === 'accepted_by_client' ? 'bg-green-100 text-green-800' : it.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : 'bg-amber-200 text-amber-900'}`}>{ISSUE_LABELS[it.status] || it.status}</span>
                      <div className="text-sm text-brand-navy">{it.label}{it.notes && <div className="text-xs text-gray-600 mt-1">{it.notes}</div>}</div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}

        {tab === 'usine' && (
          <section className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-bold text-brand-navy mb-4">A · Usine et capacités de production</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {FIELD_A.map(([k, label]) => audit[k] && (
                <Field key={k} label={label} value={audit[k]} verif={audit.infoStatus?.[k]} />
              ))}
              {audit.factoryNotes && <div className="sm:col-span-2 p-3 rounded bg-gray-50 border border-gray-200"><div className="text-[10px] uppercase font-bold text-gray-500 tracking-widest mb-1">Observations de l'auditeur</div><p className="text-sm text-brand-navy whitespace-pre-wrap">{audit.factoryNotes}</p></div>}
            </div>
            <AttachmentsBlock atts={attachments.filter((a) => a.kind === 'photo' || a.kind === 'video')} urlFor={attUrl} title="Photos & vidéos de la visite" />
          </section>
        )}

        {tab === 'produit' && (
          <section className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-bold text-brand-navy mb-4">B · Grille de conformité au cahier des charges</h2>
            {grid.length === 0 ? <p className="text-sm text-gray-400 italic">Aucune exigence documentée.</p> : (
              <div className="space-y-3">
                {grid.map((r, i) => (
                  <div key={i} className={`p-4 rounded-lg border-2 ${r.status ? COMPLIANCE_STYLES[r.status] : 'border-gray-200 bg-white'}`}>
                    <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                      <div className="font-bold text-brand-navy">{r.requirement || `Exigence ${i + 1}`}</div>
                      {r.status && <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${COMPLIANCE_STYLES[r.status]}`}>{COMPLIANCE_LABELS[r.status]}</span>}
                    </div>
                    <dl className="grid sm:grid-cols-2 gap-2 text-xs">
                      {r.verificationMethod && <div><dt className="font-bold text-gray-600">Méthode</dt><dd>{r.verificationMethod}</dd></div>}
                      {r.comment && <div><dt className="font-bold text-gray-600">Preuve / commentaire</dt><dd>{r.comment}</dd></div>}
                    </dl>
                    {r.finding && <div className="mt-2 text-sm text-brand-navy whitespace-pre-wrap"><b className="text-xs text-gray-600 uppercase tracking-wider block mb-1">Constat</b>{r.finding}</div>}
                  </div>
                ))}
              </div>
            )}
            <p className="mt-4 text-xs text-gray-500 italic">Yuville n'agit pas comme organisme de certification ni comme laboratoire. Certaines caractéristiques peuvent nécessiter un test ou une certification par un organisme indépendant — clairement indiqué dans la grille ci-dessus.</p>
          </section>
        )}

        {tab === 'juridique' && (
          <section className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-bold text-brand-navy mb-4">C · Vérification juridique et commerciale</h2>
            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              {FIELD_C.map(([k, label]) => audit[k] && (
                <Field key={k} label={label} value={audit[k]} verif={audit.infoStatus?.[k]} />
              ))}
            </div>

            {/* Entity coherence */}
            <div className={`rounded-xl border-2 p-5 ${entitiesConsistent ? 'border-green-400 bg-green-50' : audit.factoryOperator ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="font-bold text-brand-navy mb-3 flex items-center gap-2"><Shield className="w-4 h-4 text-brand-yellow" /> Cohérence des entités commerciales</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-3">
                <EntBox label="Usine / manufacturier" value={audit.factoryOperator} />
                <EntBox label="Cocontractant" value={audit.contractingEntity} />
                <EntBox label="Émetteur de facture" value={audit.invoicingEntity} />
                <EntBox label="Bénéficiaire du paiement" value={audit.paymentBeneficiary} />
              </div>
              {entitiesConsistent ? (
                <p className="text-sm text-green-800 flex items-center gap-2"><Check className="w-4 h-4" /> Les quatre entités sont identiques.</p>
              ) : audit.entityCoherence ? (
                <div className="text-sm text-brand-navy whitespace-pre-wrap bg-white p-3 rounded border border-amber-300">{audit.entityCoherence}</div>
              ) : (
                <p className="text-sm text-amber-900">Une différence entre entités n'est pas automatiquement une anomalie, mais doit être clarifiée avant la contractualisation.</p>
              )}
            </div>

            <AttachmentsBlock atts={attachments.filter((a) => a.kind === 'document')} urlFor={attUrl} title="Documents juridiques et commerciaux" />
          </section>
        )}
      </main>

      {/* Decision bar */}
      {decided ? (
        <div className="max-w-6xl mx-auto px-6 lg:px-10 mt-8">
          <div className="bg-green-50 border-2 border-green-400 rounded-xl p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-brand-navy mb-2">Décision enregistrée</h2>
            <p className="text-gray-700">Votre décision (<b>{audit.clientDecision.replace(/_/g, ' ')}</b>) a été transmise à Yuville. Nous vous recontacterons rapidement pour la suite du processus.</p>
          </div>
        </div>
      ) : (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] z-40">
          <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4">
            <div className="text-xs text-gray-500 mb-3 font-semibold uppercase tracking-widest">Votre décision sur ce manufacturier :</div>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2">
              {DECISIONS.map((d) => {
                const Icon = d.icon;
                return (
                  <button key={d.k} data-testid={`audit-decision-${d.k}`} onClick={() => setPending(d.k)} className={`inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-white text-xs font-bold ${d.color} transition`}>
                    <Icon className="w-4 h-4" /> {d.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation modal */}
      {pending && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setPending('')}>
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <AlertTriangle className="w-8 h-8 text-brand-yellow mx-auto mb-3" />
            <h3 className="text-lg font-bold text-brand-navy text-center mb-2">Confirmer votre décision ?</h3>
            <p className="text-sm text-gray-600 text-center mb-5">Vous êtes sur le point d'enregistrer la décision suivante :<br /><b className="text-brand-navy">{DECISIONS.find((d) => d.k === pending)?.label}</b></p>
            <div className="flex gap-2 justify-center">
              <button onClick={() => setPending('')} className="px-4 py-2 rounded border border-gray-300 text-brand-navy text-sm font-semibold hover:border-brand-navy">Annuler</button>
              <button onClick={() => submitDecision(pending)} className="px-5 py-2 rounded bg-brand-yellow text-brand-navy text-sm font-bold hover:bg-yellow-400">Confirmer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, verif }) {
  return (
    <div className="p-3 rounded bg-gray-50 border border-gray-200">
      <div className="text-[10px] uppercase font-bold text-gray-500 tracking-widest mb-1">{label}</div>
      <div className="text-sm text-brand-navy whitespace-pre-wrap">{value}</div>
      {verif && <div className={`inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full border ${INFO_STYLES[verif]}`}>{INFO_LABELS[verif]}</div>}
    </div>
  );
}

function SumItem({ label, value, verif }) {
  return (
    <div className="p-3 rounded border border-gray-100">
      <div className="text-[10px] uppercase font-bold text-brand-yellow tracking-widest mb-1">{label}</div>
      <div className="text-sm text-brand-navy">{value}</div>
      {verif && <div className="text-[10px] text-gray-500 mt-1 italic">{verif}</div>}
    </div>
  );
}

function EntBox({ label, value }) {
  return (
    <div className="bg-white rounded p-2 border border-gray-200 min-h-[60px]">
      <div className="text-[9px] uppercase font-bold text-gray-500 tracking-widest">{label}</div>
      <div className="text-brand-navy font-semibold text-xs mt-1 break-words">{value || '—'}</div>
    </div>
  );
}

function AttachmentsBlock({ atts, urlFor, title }) {
  if (!atts.length) return null;
  return (
    <div className="mt-6">
      <div className="text-xs font-bold uppercase tracking-widest text-brand-yellow mb-3">{title} ({atts.length})</div>
      <div className="grid sm:grid-cols-3 md:grid-cols-4 gap-3">
        {atts.map((at) => {
          const url = urlFor(at.index);
          if (at.kind === 'photo') {
            return <a key={at.index} href={url} target="_blank" rel="noreferrer" className="block group"><img src={url} alt={at.filename} className="w-full h-32 object-cover rounded border border-gray-200 group-hover:border-brand-yellow transition" /></a>;
          }
          if (at.kind === 'video') {
            return <video key={at.index} src={url} controls className="w-full h-32 object-cover rounded border border-gray-200" />;
          }
          return <a key={at.index} href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-3 bg-gray-50 rounded border border-gray-200 hover:border-brand-yellow text-xs"><FileText className="w-4 h-4 text-brand-yellow shrink-0" /><span className="truncate text-brand-navy">{at.filename}</span></a>;
        })}
      </div>
    </div>
  );
}
